import { RiLineChartLine, RiUserSearchLine, RiShieldFlashLine, RiPulseLine } from "react-icons/ri";

import { FiBarChart2, FiLayers, FiTarget, FiZap } from "react-icons/fi";

import {
  LuSettings,
  LuCircleCheck,
  LuUsers,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuShield,
  LuBuilding2,
  LuShoppingBag,
  LuClock,
  LuWallet,
  LuCpu,
  LuClipboardList,
  LuPalette,
  LuCode,
  LuBug,
  LuRocket,
} from "react-icons/lu"; // Fixed import source here // or "react-icons/lu" matching your setup

// 1. Benefits mapped directly to the layout format
export const growthBenefits = [
  {
    icon: <RiPulseLine className="w-8 h-8" />,
    title: "Predictive Intelligence",
    desc: "Move from reactive reporting to proactive growth by forecasting trends before they happen.",
  },
  {
    icon: <RiUserSearchLine className="w-8 h-8" />,
    title: "Precision Targeting",
    desc: "Identify your most profitable customer segments using advanced machine learning models.",
  },
  {
    icon: <RiShieldFlashLine className="w-8 h-8" />,
    title: "Automated Optimization",
    desc: "Continuous AI loops that adjust budgets and targeting in real-time for peak performance.",
  },
  {
    icon: <RiLineChartLine className="w-8 h-8" />,
    title: "Measurable ROI Lift",
    desc: "Clear attribution models that prove exactly how your data-driven campaigns impact revenue.",
  },
];

// 2. Services converted with required image structures, links, and point maps
export const growthServices = [
  {
    icon: <FiBarChart2 size={36} />,
    title: "Performance Analytics",
    description: "Unified dashboards that track the full customer journey from first touch to retention.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop",
    link: "/performance-analytics-services",
    points: ["Unified Dashboard Setup", "Full Funnel Tracking", "Customer Journey Mapping", "Retention Analytics"],
  },
  {
    icon: <FiLayers size={36} />,
    title: "AI Optimization",
    description: "Machine learning algorithms that scale your winning campaigns and kill the losers automatically.",
    image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?q=80&w=2070&auto=format&fit=crop",
    link: "/ai-optimization-services",
    points: ["Automated Campaign Scaling", "Algorithmic Budget Shifting", "Real-time Bid Adjustments", "Machine Learning Infrastructure"],
  },
  {
    icon: <FiTarget size={36} />,
    title: "Conversion Intelligence",
    description: "Deep-dive A/B testing and behavioral analysis to maximize every dollar spent.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2070&auto=format&fit=crop",
    link: "/conversion-intelligence-services",
    points: ["Advanced A/B Testing", "Behavioral Heatmaps", "Funnel Drop-off Analysis", "ROI Maximization Frameworks"],
  },
  {
    icon: <FiZap size={36} />,
    title: "Predictive Modeling",
    description: "Forecast future revenue and lead volume with high-accuracy statistical models.",
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2070&auto=format&fit=crop",
    link: "/predictive-modeling-services",
    points: ["Revenue Forecasting Models", "Lead Volume Predictions", "Statistical Trend Analysis", "Historical Data Extrapolation"],
  },
];

// 3. Process Steps structured identically to the web formatting
export const growthProcessSteps = [
  {
    step: "01",
    title: "Data Audit",
    description: "Cleaning and structuring your existing data for AI readiness.",
    points: ["Source integration", "Data cleaning", "KPI definition"],
  },
  {
    step: "02",
    title: "Model Building",
    description: "Developing custom machine learning models tailored to your business goals.",
    points: ["Algorithm selection", "Feature engineering", "Initial testing"],
  },
  {
    step: "03",
    title: "Insight Scaling",
    description: "Deploying automated loops to optimize campaigns at scale.",
    points: ["Budget automation", "Creative testing", "Trend prediction"],
  },
  {
    step: "04",
    title: "ROI Reporting",
    description: "Monthly strategic deep-dives into ROI and predictive accuracy.",
    points: ["Attribution review", "Performance audit", "Future mapping"],
  },
];

// 4. Added Hero block specifically tailored for Growth Analytics
export const growthHeroData = {
  backgroundImage: "/images/serviceBanners/GrowthAnalytics.webp",
  whiteImage: "/images/serviceBanners/GrowthAnalyticsW.webp",
  subTitle: "Growth & Analytics AI Services",
  title: (
    <>
      Accelerate Business Growth with <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Predictive AI Solutions</span>
    </>
  ),
  description:
    "We build predictive AI analytics solutions that move beyond reactive reporting to deliver intelligent forecasting, advanced attribution modeling, and optimization systems for smarter, data driven business growth and decision making.",
  primaryButton: {
    title: "Get Growth Strategy",
    onClick: () => console.log("Growth Strategy clicked"),
  },
  secondaryButton: {
    title: "View Case Studies",
    onClick: () => console.log("View Case Studies clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Predictive Engine",
      desc: "Forecast trends and conversions before they happen.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Real-Time Loops",
      desc: "Continuous AI-driven adjustments for live campaign performance.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Enterprise Grade",
      desc: "Secure, encrypted, and fully compliant data pipelines.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Clear Attribution",
      desc: "Understand exactly where growth and revenue are coming from.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp",
      top: "16%",
      right: "6%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(168,85,247,0.35)",
      iconGlow: "rgba(168,85,247,0.9)",
    },
    {
      image: "/images/services/website/python.svg",
      top: "20%",
      right: "20%",
      containerSize: 60,
      iconSize: 40,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};

// 5. Stats specific to growth analytics structures
export const growthStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 6. Why Choose Us block mapping for growth parameters
export const growthSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE OPTIMIZE",
  buildTitle: "Data & AI Solutions for Smarter Business Growth",
  buildCards: [
    {
      title: "Growth Funnels",
      desc: "Track the full customer journey with data-driven growth funnel insights.",
      icon: LuSmartphone,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Enterprise Modeling",
      desc: "Scalable algorithmic systems built to manage complex customer data.",
      icon: LuBuilding2,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce Intelligence",
      desc: "Smarter insights through behavioral tracking, pricing automation, and predictive systems.",
      icon: LuShoppingBag,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Real-Time Attribution",
      desc: "Track conversion shifts with clear, data-backed attribution insights.",
      icon: LuClock,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Predictive Analytics Engine",
      desc: "Forecast user behavior using AI-driven models for better decision making.",
      icon: LuSmartphone, // Matching design palette
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Data Visualization Layer",
      desc: "Convert complex data into clear, actionable dashboards and reports.",
      icon: LuBuilding2,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR DATA & AI STRATEGY LIFECYCLE",
  processTitle: "Our Data & AI Strategy Lifecycle",
  processSteps: [
    {
      step: "01",
      title: "Audit",
      description: "Clean and structure data for better system readiness.",
      icon: LuUsers,
    },
    {
      step: "02",
      title: "Build",
      description: "Custom ML models designed around specific business goals.",
      icon: LuClipboardList,
    },
    {
      step: "03",
      title: "Scale",
      description: "Automated systems built to support operational growth.",
      icon: LuPalette,
    },
    {
      step: "04",
      title: "Report",
      description: "Clear metrics to measure performance and attribution.",
      icon: LuCode,
    },
    {
      step: "05",
      title: "Data Engineering",
      description: "Prepare and optimize data pipelines for AI-driven systems cleanly.",
      icon: LuCircleCheck, // Swapped with compile-safe Lucide icon variant to protect pipeline integrity
    },
    {
      step: "06",
      title: "Model Optimization",
      description: "Improve accuracy and efficiency of machine learning models systematically.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE PERFORMANCE ENGINE?",
  benefitsTitle: "Why Choose Our Performance Engine?",
  benefits: [
    "Continuous ML-driven performance optimization",
    "Advanced touchpoint attribution insights",
    "Reduced wasted ad spend through smarter automation",
    "Optimized system performance with lower latency",
    "Clear audience segmentation and cohort insights",
    "Predictive trend analysis for proactive decisions",
  ],
  logoImage: "/images/serviceBanners/GrowthAnalytics.webp",
  whiteLogoImage: "/images/serviceBanners/GrowthAnalyticsW.webp",
};

// 7. Portfolio projects mapped over for Growth case studies
export const growthPortfolioData = {
  subtitle: "OUR CASE STUDIES",
  title: "Growth Metrics We Are Proud Of",
  buttonText: "View All Projects",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "ScaleMetrics Inc.",
      badge: "SaaS Scaling Pipeline",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Constructing zero-loss data capture pipelines resulting in complete clarity over marketing budgets.",
      downloads: "3.2x ROI",
      rating: "5.0",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop",
    },
    {
      title: "OmniRetail Global",
      badge: "E-Commerce Optimization",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "Injecting cross-channel behavioral algorithms to drive automated multi-million variant checkout setups.",
      downloads: "+45% Rev",
      rating: "4.9",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2070&auto=format&fit=crop",
    },
  ],
};

// 8. Frameworks used inside the engineering setup
export const growthTechnologiesData = {
  techTitle: "DATA SYSTEM ARCHITECTURES",
  technologies: [
    { name: "Python", icon: "Python" },
    { name: "TensorFlow", icon: "TensorFlow" },
    { name: "PostgreSQL", icon: "PostgreSQL" },
    { name: "Tableau", icon: "Tableau" },
    { name: "Google Analytics", icon: "GA4" },
    { name: "Mixpanel", icon: "Mixpanel" },
  ],
  trustTitle: "TRUSTED BY LEADING BRANDS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

// 9. About overview matching the exact standard text parameters
export const growthAboutData = [
  {
    title: "Smarter Decisions with AI Analytics Services",
    content: (
      <>
        Modern businesses can no longer rely on delayed reports to make growth decisions. With advanced <b className="text-text-primary font-semibold">AI analytics services</b>,
        brands can identify customer behavior patterns, predict demand shifts, and improve campaign performance before opportunities are missed. Businesses seeking long-term
        scalability are now moving toward intelligent systems that turn raw information into measurable outcomes. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, we focus on building predictive ecosystems that help companies improve operational visibility while
        supporting faster, smarter business decisions through tailored automation.
      </>
    ),
  },
  {
    title: "Business Intelligence Solutions for Measurable Growth",
    content: (
      <>
        Reliable <b className="text-text-primary font-semibold">business intelligence solutions</b> help organizations understand what drives conversions, customer retention, and
        overall business performance. Instead of depending on fragmented reports, companies benefit from centralized insights that simplify decision-making. A trusted{" "}
        <b className="text-text-primary font-semibold">AI data analytics company</b> helps uncover hidden growth opportunities by analyzing customer interactions, behavioral
        patterns, and operational inefficiencies. Businesses working with Tycho Technologies often experience stronger visibility into performance trends, helping leadership teams
        make more confident, data-backed decisions.
      </>
    ),
  },
  {
    title: "Predictive Analytics Services That Improve Performance",
    content: (
      <>
        Growth becomes more predictable when businesses invest in advanced <b className="text-text-primary font-semibold">predictive analytics services</b> built around real
        customer behavior. Forecasting future trends allows brands to reduce guesswork and respond proactively to market changes. As an experienced{" "}
        <b className="text-text-primary font-semibold">AI analytics company in india</b>, businesses expect scalable systems that support long-term expansion without increasing
        operational complexity. Many organizations also align these systems with a trusted{" "}
        <a href="https://tychotechnologies.com/seo-content-strategy-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          seo services company
        </a>{" "}
        to ensure growth strategies are supported across channels while improving digital visibility naturally.
      </>
    ),
  },
  {
    title: "Why Businesses Trust Data Analytics Services India",
    content: (
      <>
        The demand for <b className="text-text-primary font-semibold">data analytics services india</b> continues to grow as businesses seek faster insights and stronger
        performance clarity. Companies want secure, intelligent systems that support real-time decisions while improving customer understanding. Working with Tycho Technologies
        gives organizations access to practical AI-driven frameworks designed for measurable outcomes. Businesses also benefit when data strategies work alongside a reliable{" "}
        <a href="https://tychotechnologies.com/digital-marketing-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          digital marketing company in india
        </a>
        , creating a stronger foundation for sustainable business growth.
      </>
    ),
  },
];
// 10. Existing FAQs preserved intact
export const growthFaqData = [
  {
    q: "How much data do I need for AI to be effective?",
    a: "While more data helps, we can start building effective predictive models with as little as 3 months of consistent conversion data.",
  },
  {
    q: "Will this replace my marketing team?",
    a: "No. Our AI acts as a force multiplier, automating the tedious optimization tasks so your team can focus on creative strategy.",
  },
  {
    q: "Is my data secure?",
    a: "Absolutely. We implement enterprise-grade encryption and comply with all GDPR/CCPA regulations for data handling.",
  },
];
